/* ══ F15 STEP 3.7 — THE SERVER WRITES NEXT WEEK, EXACTLY AS THE PHONE WOULD ══════════════════════════════════════════════
 * The I/O half of `CoachNextWeekService.swift` + `CoachAutoApplyRunner.swift`, statement for statement and in the SAME
 * ORDER (the service's five decisions; its header says why the order is load-bearing). The pure half — `plan`, `review`,
 * `bind`, `persist` — is `coachPlan.ts` + `coachNextWeek.ts`, pinned to the Swift by `progression-fixture.json` (cases,
 * chains, and each one's `writer` block: the writes, deletions and moves the automatic path makes).
 *
 * 🔴 EVERY READ AND WRITE GOES THROUGH THE CLIENT IT IS HANDED, AND `apply-next-week` HANDS IT THE COACH'S OWN JWT — so RLS
 * is the authority exactly as it is on the phone, and this file can do nothing the coach's phone could not.
 * ⚠️ NOTHING HERE NOTIFIES (decision 5): no coach performed an act, so "your coach assigned you this" would not be true.
 * ⚠️ THE CHECK-IN IS `.empty`, AS THE PHONE'S RUNNER STATES IT: no coached client can report pain yet (MEASURED 10-30).
 * ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════ */

import {
  bestLoggedSession, due, weekStart, type Assigned, type Due, type LoggedSession, type Row, type SectionRole,
} from "./progression.ts";
import { plan, type CheckIn, type Plan, type PriorDay } from "./coachPlan.ts";
import { ladderChain, type LadderTarget } from "./coachLadder.ts";
import { tabNextIds } from "./coachTabs.ts";
import {
  automaticDecisions, bind, explain, persist, readiness, readingOrder as readingOrderOf, review, type ExistingSet,
  type Review,
} from "./coachNextWeek.ts";
import { effectiveRule, type WorkoutRulesEngine, PROGRESS_DEFAULT } from "./workoutRules.ts";
import { CALENDAR_SENTENCE, deloadIsDue, deloadTriggered, headIndex, isAutoBlock, programBlockDue, programVerdict,
         programWeekOf, TRIGGER_SENTENCE, triggerCandidates, triggerSets, type ProgramAssignmentRow, type ProgramBlock,
         type ProgramDayWeekRow, type ProgramVerdict, type ProgramWeekRow, type TriggerSession, weeksSinceDeload }
  from "./coachDeload.ts";

/* deno-lint-ignore no-explicit-any */
type Any = any;
/** The two calls this file makes on a Supabase client — structural, so a test can hand it a recorder. */
export interface Db {
  from(table: string): Any;
  rpc(fn: string, args: Record<string, unknown>): Any;
}

export const EMPTY_CHECK_IN: CheckIn = { effort: "aboutRight", pain: {} };

async function rows<T>(q: PromiseLike<{ data: T[] | null; error: { message: string; code?: string } | null }>): Promise<T[]> {
  const { data, error } = await q;
  if (error) throw new WriterError(error.message, error.code);
  return data ?? [];
}

export class WriterError extends Error {
  constructor(message: string, readonly code?: string) { super(message); }
}

// ── The reads, as `CoachNextWeekService`'s `// MARK: - Reads` ──────────────────────────────────────────────────────────

/** `engine(ofRoot:)` — the ROOT's switch and rules (F15 step 3.2). No switch row = the default (ON), no cadence. */
export async function readEngine(db: Db, rootWorkoutId: string): Promise<WorkoutRulesEngine> {
  const switches = await rows<{ progress: boolean; deload_every: number | null }>(
    db.from("coach_workout_engine").select("progress, deload_every").eq("workout_id", rootWorkoutId));
  const rules = await rows<{ exercise_id: string; progression_config: unknown }>(
    db.from("coach_workout_rules").select("exercise_id, progression_config").eq("workout_id", rootWorkoutId));
  return engineOf(switches, rules);
}

/** The pure half of `readEngine` (F15 3.9: the program board reads many roots in ONE request per table and assembles each
 *  root's engine here — the same assembly, never a second reading of the rows). */
export function engineOf(switches: { progress: boolean; deload_every: number | null }[],
                         rules: { exercise_id: string; progression_config: unknown }[]): WorkoutRulesEngine {
  const map = new Map<string, unknown>();
  // `uniquingKeysWith: { first, _ in first }` — the first row for an exercise wins.
  for (const r of rules) {
    const key = r.exercise_id.toLowerCase();
    if (!map.has(key)) map.set(key, r.progression_config ?? null);
  }
  return { progress: switches[0]?.progress ?? PROGRESS_DEFAULT, rules: map, deloadEvery: switches[0]?.deload_every ?? null };
}

/** `section_type` → `SectionRole`; `workout` (and anything unrecognised) is main — the service's own reading. */
export function sectionRole(sectionType: string | null | undefined): SectionRole {
  switch (sectionType) {
    case "warm_up": return "warm_up";
    case "cool_down": return "cool_down";
    case "recovery": return "recovery";
    default: return "main";
  }
}

interface SetRowDb {
  id: string; position: number; reps: number | null; weight_kg: number | null; time_seconds: number | null;
  rest_seconds: number | null; set_type: string | null; rounds: number | null; distance_m: number | null;
}
interface ExRowDb {
  id: string; exercise_id: string; position: number; hold_seconds_min: number | null; reps_min: number | null;
  superset_with_next: boolean | null; coach_workout_sets: SetRowDb[] | null;
  /** F15 4.1b — the row's own fields a rule tab writes; absent on a read without them = as written. */
  tempo?: string | null; notes?: string | null; each_side?: boolean | null; hidden?: boolean | null;
}
export interface SectionRowDb { position: number; section_type: string | null; coach_workout_exercises: ExRowDb[] | null }
export interface LibraryRowDb {
  id: string; name: string; progression_config: unknown; is_quality_limited: boolean | null;
  movement_pattern: string | null; primary_muscles: string[] | null;
}

export interface WorkoutRows { rows: Row[]; setsByRow: Record<string, ExistingSet[]> }

/** `rows(ofWorkout:engine:)` minus the I/O — pure, so a test pins the mapping. Ids are compared lower-case. */
export function workoutRows(sections: SectionRowDb[], library: LibraryRowDb[], engine: WorkoutRulesEngine): WorkoutRows {
  const byId = new Map(library.map((l) => [l.id.toLowerCase(), l]));
  const out: Row[] = [];
  const setsByRow: Record<string, ExistingSet[]> = {};
  for (const section of sections) {
    const role = sectionRole(section.section_type);
    for (const ex of [...(section.coach_workout_exercises ?? [])].sort((a, b) => a.position - b.position)) {
      const lib = ex.exercise_id.toLowerCase();
      const facts = byId.get(lib);
      const ordered = [...(ex.coach_workout_sets ?? [])].sort((a, b) => a.position - b.position);
      const measured = ordered.some((s) => s.rounds !== null || s.distance_m !== null);
      out.push({
        id: ex.id.toLowerCase(),
        libraryExerciseId: lib,
        name: facts?.name ?? "?",
        sectionRole: role,
        ordinal: section.position * 1000 + ex.position,
        sets: ordered.map((s) => ({
          position: s.position, reps: s.reps, weightKg: s.weight_kg, timeSeconds: s.time_seconds,
          restSeconds: s.rest_seconds, setType: s.set_type ?? "regular",
        })),
        supersetWithNext: ex.superset_with_next ?? false,
        // `isHold && !measuredInDistanceOrRounds` — the Swift Row's own init (step 3b: a run's time is never a hold).
        isHold: ex.hold_seconds_min !== null && ex.reps_min === null && !measured,
        rule: effectiveRule(engine, lib, facts?.progression_config ?? null).config,
        isQualityLimited: facts?.is_quality_limited ?? null,
        movementPattern: facts?.movement_pattern ?? null,
        measuredInDistanceOrRounds: measured,
        // F15 4.1b — the row's own fields (a hidden row is left out of `pair`; a tab may write them).
        tempo: ex.tempo ?? null, note: ex.notes ?? null, eachSide: ex.each_side ?? false, hidden: ex.hidden ?? false,
      });
      setsByRow[ex.id.toLowerCase()] = ordered.map((s) => ({
        id: s.id.toLowerCase(), position: s.position, carriesUnmodelledColumns: s.rounds !== null || s.distance_m !== null,
      }));
    }
  }
  return { rows: out.sort((a, b) => a.ordinal - b.ordinal), setsByRow };
}

export const LIBRARY_COLUMNS = "id, name, progression_config, is_quality_limited, movement_pattern, primary_muscles";

/** The section tree `rowsOfWorkout` reads (exported for the board's batched read, F15 3.9 — one select, one shape). */
export const SECTION_TREE_SELECT = "position, section_type, coach_workout_exercises(id, exercise_id, position, hold_seconds_min, "
  + "reps_min, superset_with_next, tempo, notes, each_side, hidden, coach_workout_sets(id, position, reps, weight_kg, "
  + "time_seconds, rest_seconds, set_type, rounds, distance_m))";

export async function rowsOfWorkout(db: Db, workoutId: string, engine: WorkoutRulesEngine): Promise<WorkoutRows> {
  const sections = await rows<SectionRowDb>(db.from("coach_workout_sections").select(SECTION_TREE_SELECT)
    .eq("workout_id", workoutId));
  const ids = [...new Set(sections.flatMap((s) => (s.coach_workout_exercises ?? []).map((e) => e.exercise_id)))];
  const library = ids.length === 0 ? []
    : await rows<LibraryRowDb>(db.from("coach_exercises").select(LIBRARY_COLUMNS).in("id", ids));
  return workoutRows(sections, library, engine);
}

interface LogRowDb {
  set_number: number; reps_completed: number | null; hold_seconds: number | null; weight_kg: number | null;
  rpe: number | null; status: string | null; modifier_label_override: string | null;
}
export interface SessionRowDb {
  scheduled_date: string | null; performance_notes: string | null;
  workout_session_exercises: { position: number; coach_exercise_id: string | null; set_logs: LogRowDb[] | null }[] | null;
}

/** A `date` column as the planner's ISO instant (midnight UTC of that day), the one form `dateText` reads. */
export const isoOfDay = (day: string | null): string => `${day ?? "1970-01-01"}T00:00:00.000Z`;

/** `logged(clientId:on:)` minus the I/O. */
export function loggedSession(sessions: SessionRowDb[]): LoggedSession | null {
  return bestLoggedSession(sessions.map((s) => ({
    date: isoOfDay(s.scheduled_date),
    performanceNotes: s.performance_notes,
    rows: [...(s.workout_session_exercises ?? [])].sort((a, b) => a.position - b.position).map((e) => ({
      position: e.position,
      libraryExerciseId: e.coach_exercise_id?.toLowerCase() ?? null,
      sets: [...(e.set_logs ?? [])].sort((a, b) => a.set_number - b.set_number).map((l) => ({
        reps: l.reps_completed, holdSeconds: l.hold_seconds, weightKg: l.weight_kg, rpe: l.rpe,
        status: l.status ?? "completed", bandLabel: l.modifier_label_override,
      })),
    })),
  })));
}

async function logged(db: Db, clientId: string, day: string | null): Promise<LoggedSession | null> {
  if (day === null) return null;
  const sessions = await rows<SessionRowDb>(db.from("workout_sessions").select(
    "scheduled_date, performance_notes, workout_session_exercises(position, coach_exercise_id, set_logs(set_number, "
    + "reps_completed, hold_seconds, weight_kg, rpe, status, modifier_label_override))")
    .eq("user_id", clientId).eq("scheduled_date", day).order("created_at", { ascending: true }));
  return loggedSession(sessions);
}

async function muscles(db: Db, dayRows: Row[]): Promise<Record<string, string[]>> {
  const ids = [...new Set(dayRows.map((r) => r.libraryExerciseId))];
  if (ids.length === 0) return {};
  const library = await rows<LibraryRowDb>(db.from("coach_exercises").select(LIBRARY_COLUMNS).in("id", ids));
  return Object.fromEntries(library.map((l) => [l.id.toLowerCase(), l.primary_muscles ?? []]));
}

/** `ladderTargets(forRows:)` — the library rows each ladder's FIRST link names; a deleted one is simply absent. */
async function ladderTargets(db: Db, dayRows: Row[]): Promise<Record<string, LadderTarget>> {
  /* F15 step 4.1 — and every exercise a row's TABS name as a `next` step (`tabNextIds`). */
  const named = [...dayRows.map((r) => ladderChain(r.rule)?.links[0]?.next), ...dayRows.flatMap((r) => tabNextIds(r.rule))];
  const ids = [...new Set(named.filter((x): x is string => !!x)
    .filter((x) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(x)))];
  if (ids.length === 0) return {};
  const found = await rows<{ id: string; name: string; is_hold: boolean | null; typical_rep_range_min: number | null;
                             typical_hold_seconds_min: number | null }>(
    db.from("coach_exercises").select("id, name, is_hold, typical_rep_range_min, typical_hold_seconds_min").in("id", ids));
  const out: Record<string, LadderTarget> = {};
  for (const t of found) {
    const key = t.id.toLowerCase();
    if (out[key] === undefined) {
      out[key] = { id: key, name: t.name, isHold: t.is_hold ?? false, repsMin: t.typical_rep_range_min,
                   holdSecondsMin: t.typical_hold_seconds_min };
    }
  }
  return out;
}

/** F15 4.2 — the program covering the client's week holding `targetDate`, its BLOCK (`coach_programs.progress ·
 *  deload_every · deload_automatic`) and its own week rows; null when no program the caller gave covers it (the workout's
 *  own row decides then). Read as the caller, the same three tables `programMark` reads, then the one program row. */
export interface ProgramAt { programId: string; weekIndex: number; block: ProgramBlock; weeks: ProgramWeekRow[] }

export async function programAt(db: Db, clientId: string, targetDate: string): Promise<ProgramAt | null> {
  const given = await rows<ProgramAssignmentRow>(
    db.from("coach_program_assignments").select("program_id, start_date").eq("client_id", clientId));
  if (given.length === 0) return null;
  const ids = [...new Set(given.map((g) => g.program_id))];
  const weeks = await rows<ProgramWeekRow>(
    db.from("coach_program_weeks").select("program_id, week_index, is_deload, derived").in("program_id", ids));
  const days = await rows<ProgramDayWeekRow>(
    db.from("coach_program_days").select("program_id, week_index").in("program_id", ids));
  const at = programWeekOf(given, weeks, days, targetDate);
  if (at === null) return null;
  const found = await rows<{ progress: boolean; deload_every: number | null; deload_automatic: boolean }>(
    db.from("coach_programs").select("progress, deload_every, deload_automatic").eq("id", at.programId));
  const b = found[0];
  if (b === undefined) return null;
  return { programId: at.programId, weekIndex: at.weekIndex,
           block: { progress: b.progress, deloadEvery: b.deload_every, automatic: b.deload_automatic },
           weeks: weeks.filter((w) => w.program_id === at.programId) };
}

/** F15 4.2 — the logged numbers (`deloadTriggered`): the client's assignments before the target's week, most recent first
 *  (`triggerCandidates`), each one's working sets against its log (`triggerSets`), until two hold a working set. */
async function numbersFell(db: Db, clientId: string, targetDate: string, engine: WorkoutRulesEngine): Promise<boolean> {
  const all = (await rows<{ id: string; workout_id: string; scheduled_date: string | null; is_deload: boolean | null }>(
    db.from("coach_assignments").select("id, workout_id, scheduled_date, is_deload").eq("client_id", clientId)))
    .filter((a) => a.scheduled_date !== null);
  const byId = new Map(all.map((a) => [a.id, a]));
  const sessions: TriggerSession[] = [];
  for (const c of triggerCandidates(all.map((a) => ({ id: a.id, date: a.scheduled_date as string,
                                                     isDeload: a.is_deload ?? false })), targetDate)) {
    if (sessions.length === 2) break;
    const a = byId.get(c.id);
    if (a === undefined) continue;
    const sets = triggerSets((await rowsOfWorkout(db, a.workout_id, engine)).rows, await logged(db, clientId, a.scheduled_date));
    if (sets.length > 0) sessions.push({ date: c.date, isDeload: false, sets });
  }
  return deloadTriggered(sessions, targetDate);
}

/** The Monday of the week holding `day` — a proposal's `week_start`. */
export const mondayOf = (day: string): string => weekStart(day.slice(0, 10));

// ── Step 1 — `prepare` ────────────────────────────────────────────────────────────────────────────────────────────────

export interface Prepared {
  review: Review;
  plan: Plan;
  clientId: string;
  sourceWorkoutId: string;
  rootWorkoutId: string;
  sourceRows: Row[];
  engine: WorkoutRulesEngine;
  /** F15 4.2 — the program block's word for this week (null = outside a program: the workout's own row decided). */
  program: (ProgramVerdict & { programId: string; weekStart: string }) | null;
}

/** `prepare(rootWorkoutId:clientId:weekNumber:targetDate:checkIn:)`. `targetDate` is the day being written (`Due`). */
export async function prepare(db: Db, p: { rootWorkoutId: string; clientId: string; weekNumber: number; targetDate: string;
                                           checkIn?: CheckIn }): Promise<Prepared> {
  const root = p.rootWorkoutId;
  const fetched = (await rows<{ id: string; workout_id: string; root_workout_id: string | null; scheduled_date: string | null;
                               is_deload: boolean | null }>(
    db.from("coach_assignments").select("id, client_id, workout_id, root_workout_id, scheduled_date, is_deload")
      .eq("client_id", p.clientId)
      .or(`root_workout_id.eq.${root},and(root_workout_id.is.null,workout_id.eq.${root})`)
      .order("scheduled_date", { ascending: false })))
    /* F15 3.9 — NEVER A HEAD DATED ON OR AFTER THE DAY BEING WRITTEN: a program pre-assigns its WRITTEN weeks, so an auto
       week before a written one would otherwise progress from the FUTURE copy (MEASURED by reading: `order desc` puts the
       latest copy first). An undated row keeps its place, as before. Swift: the same filter in `CoachNextWeekService`. */
    .filter((a) => a.scheduled_date === null || a.scheduled_date.slice(0, 10) < p.targetDate.slice(0, 10));
  const headAt = headIndex(fetched.map((a) => a.is_deload ?? false));
  if (headAt === null) throw new WriterError("That workout has never been assigned, so there is no week to progress from.");
  const assignments = fetched.slice(headAt);
  const head = assignments[0];
  if (head === undefined) throw new WriterError("That workout has never been assigned, so there is no week to progress from.");
  /* F15 4.2 — INSIDE A PROGRAM THE PROGRAM'S BLOCK DECIDES: its switch replaces the workout's (before any row is read, as
     the switch decides each row's rule), its marks / cadence / the numbers the deload. Outside, the workout's own row. */
  const read = await readEngine(db, head.root_workout_id ?? head.workout_id);
  const at = await programAt(db, p.clientId, p.targetDate);
  const engine: WorkoutRulesEngine = at === null ? read : { ...read, progress: at.block.progress };

  const days: PriorDay[] = [];
  for (const a of assignments) {
    const r = await rowsOfWorkout(db, a.workout_id, engine);
    days.push({
      day: { assignmentId: a.id.toLowerCase(), date: isoOfDay(a.scheduled_date ?? "0001-01-01"),
             rootWorkoutId: (a.root_workout_id ?? a.workout_id).toLowerCase(), rows: r.rows },
      logged: await logged(db, p.clientId, a.scheduled_date),
    });
  }
  const today = days[0];
  if (today === undefined) throw new WriterError("That workout has never been assigned, so there is no week to progress from.");
  const calendar = (await rows<{ scheduled_date: string | null; is_deload: boolean | null }>(
    db.from("coach_assignments").select("scheduled_date, is_deload").eq("client_id", p.clientId)))
    .filter((r) => r.scheduled_date !== null)
    .map((r) => ({ date: r.scheduled_date as string, isDeload: r.is_deload ?? false }));
  let isDeload: boolean;
  let program: Prepared["program"] = null;
  if (at === null) {
    isDeload = deloadIsDue(engine.deloadEvery, engine.progress, calendar, p.targetDate);
  } else {
    const targetWeek = mondayOf(p.targetDate);
    const weekMarked = calendar.some((d) => d.isDeload && mondayOf(d.date) === targetWeek);
    const byBlock = at.block.progress && (weekMarked || programBlockDue(at.block, at.weeks, at.weekIndex));
    /* 11-02 — only AUTO reads the numbers (MANUAL is the board's switches alone), so a Manual week reads no logs. */
    const triggered = at.block.progress && isAutoBlock(at.block) && !byBlock
      && await numbersFell(db, p.clientId, p.targetDate, engine);
    const verdict = programVerdict(at.block, at.weeks, at.weekIndex, weekMarked, triggered,
                                   weeksSinceDeload(at.weekIndex, calendar, p.targetDate));
    program = { ...verdict, programId: at.programId, weekStart: targetWeek };
    isDeload = verdict.isDeload;
  }
  const built = plan({
    day: today.day,
    logged: today.logged,
    history: days.slice(1),
    musclesByExerciseId: await muscles(db, today.day.rows),
    checkIn: p.checkIn ?? EMPTY_CHECK_IN,
    weekNumber: p.weekNumber,
    isDeload,
    progressSwitchedOff: !engine.progress,
    ladderTargets: await ladderTargets(db, today.day.rows),
    mint: (() => { let n = 0; return () => `introduced-${++n}`; })(),
  });
  /* F15 4.2 — the numbers' sentence first among what the coach reads (written or proposed); the proposal row itself is
     written by `proposeDeload` at the write, so a dry `prepare` writes nothing. */
  if (program?.byTrigger === true) built.proposals = [TRIGGER_SENTENCE, ...built.proposals];
  else if (program?.proposed === true) built.proposals = [CALENDAR_SENTENCE, ...built.proposals];
  return { review: review(built), plan: built, clientId: p.clientId, sourceWorkoutId: head.workout_id,
           rootWorkoutId: head.root_workout_id ?? head.workout_id, sourceRows: today.day.rows, engine, program };
}

// ── Steps 2 and 3 — the automatic path, `applyAutomatically` → `write` ─────────────────────────────────────────────────

export type Outcome =
  | { kind: "wrote"; assignmentId: string; rows: number; setsWritten: number; setsRemoved: number; moves: number;
      rowEdits: number }
  | { kind: "repeatedUnchanged"; assignmentId: string }
  | { kind: "refusedBeforeCreating"; reason: string }
  | { kind: "createdButNotWritten"; assignmentId: string; reason: string };

async function exec(q: PromiseLike<{ error: { message: string; code?: string } | null }>): Promise<void> {
  const { error } = await q;
  if (error) throw new WriterError(error.message, error.code);
}

async function cloneLastWeek(db: Db, prepared: Prepared, scheduledDate: string): Promise<string> {
  const { data, error } = await db.rpc("assign_coach_workout", {
    p_workout_id: prepared.sourceWorkoutId, p_client_id: prepared.clientId, p_scheduled_date: scheduledDate, p_note: null,
  });
  if (error) throw new WriterError(error.message, error.code);
  return String(data).toLowerCase();
}

const setHidden = (db: Db, id: string, hidden: boolean) =>
  exec(db.from("coach_assignments").update({ hidden_from_client: hidden }).eq("id", id));
const markDeload = (db: Db, id: string) => exec(db.from("coach_assignments").update({ is_deload: true }).eq("id", id));

/** The uuid a set INSERT is given — `UUID()` on the phone; `crypto.randomUUID()` here (both runtimes have it). */
export const newSetId = (): string => crypto.randomUUID();

/** `applyAutomatically(_:consent:scheduledDate:note:)` — every suggestion accepted, PUBLISHED (his 09-10 word). */
export async function applyAutomatically(db: Db, prepared: Prepared, scheduledDate: string): Promise<Outcome> {
  const decisions = automaticDecisions(prepared.review);
  const ready = readiness(prepared.review, decisions);
  if (ready.kind === "nothingAccepted") {
    const assignmentId = await cloneLastWeek(db, prepared, scheduledDate);
    if (prepared.plan.isDeloadWeek) await markDeload(db, assignmentId);
    return { kind: "repeatedUnchanged", assignmentId };
  }
  if (ready.kind === "undecided") {
    return { kind: "refusedBeforeCreating",
             reason: `The automatic decisions left ${ready.names.join(", ")} unanswered, which cannot happen; nothing was created.` };
  }
  // ── `write(_:decisions:scheduledDate:note:publish: true)` ──
  const preflight = bind(prepared.review, decisions, prepared.sourceRows);
  if (preflight.kind !== "bound") return { kind: "refusedBeforeCreating", reason: explain(preflight) };

  const assignmentId = await cloneLastWeek(db, prepared, scheduledDate);
  await setHidden(db, assignmentId, true);
  if (prepared.plan.isDeloadWeek) await markDeload(db, assignmentId);

  let created: WorkoutRows;
  try {
    const a = await rows<{ workout_id: string }>(db.from("coach_assignments").select("workout_id").eq("id", assignmentId));
    if (a.length === 0) throw new WriterError("That workout has never been assigned, so there is no week to progress from.");
    const copy = a[0];
    if (copy === undefined) throw new WriterError("That workout has never been assigned, so there is no week to progress from.");
    created = await rowsOfWorkout(db, copy.workout_id, prepared.engine);
  } catch (e) {
    return { kind: "createdButNotWritten", assignmentId,
             reason: `Next week was created and is hidden, but its rows could not be read back: ${(e as Error).message}` };
  }
  const binding = bind(prepared.review, decisions, created.rows);
  if (binding.kind !== "bound") {
    return { kind: "createdButNotWritten", assignmentId,
             reason: "Next week was created and is hidden, and then the numbers would not bind to it even though the same "
               + "bind succeeded against last week moments earlier — so the clone is not the copy it should be. "
               + explain(binding) };
  }
  const persistence = persist(binding.writes, created.setsByRow);
  if (persistence.kind !== "ready") {
    return { kind: "createdButNotWritten", assignmentId,
             reason: `Next week was created and is hidden, and nothing was written into it: ${persistence.reason}` };
  }
  const p = persistence.persistence;
  try {
    await exec(db.from("coach_workout_sets").upsert(p.writes.map((w) => ({
      id: w.id ?? newSetId(), workout_exercise_id: w.rowId, position: w.set.position, reps: w.set.reps,
      weight_kg: w.set.weightKg, time_seconds: w.set.timeSeconds, rest_seconds: w.set.restSeconds, set_type: w.set.setType,
    })), { onConflict: "id" }));
    if (p.deletions.length > 0) await exec(db.from("coach_workout_sets").delete().in("id", p.deletions));
  } catch (e) {
    return { kind: "createdButNotWritten", assignmentId,
             reason: `Next week was created and is hidden, and writing the sets failed part of the way through: `
               + `${(e as Error).message}. Nothing was deleted before the writes, so the week holds last week's numbers or `
               + "the new ones, never an exercise with nothing in it." };
  }
  // F15 4.1b — a rule tab's row edit (tempo · notes · each side · hidden), after the sets and before the moves; only the
  // fields the edit names are sent (an absent key leaves the column alone).
  try {
    for (const e of p.rowEdits) {
      const patch: Record<string, unknown> = {};
      if (e.edit.tempo !== null) patch["tempo"] = e.edit.tempo;
      if (e.edit.note !== null) patch["notes"] = e.edit.note;
      if (e.edit.eachSide !== null) patch["each_side"] = e.edit.eachSide;
      if (e.edit.hidden !== null) patch["hidden"] = e.edit.hidden;
      await exec(db.from("coach_workout_exercises").update(patch).eq("id", e.rowId));
    }
  } catch (e) {
    return { kind: "createdButNotWritten", assignmentId,
             reason: "Next week was created and is hidden; its sets were written, and changing a row's own fields (tempo, "
               + `note, each side, paused) failed: ${(e as Error).message}. That row keeps its old fields.` };
  }
  try {
    for (const m of p.moves) {
      await exec(db.from("coach_workout_exercises").update({
        exercise_id: m.exerciseId, reps_min: m.repsMin, reps_max: m.repsMax,
        hold_seconds_min: m.holdSecondsMin, hold_seconds_max: m.holdSecondsMax,
      }).eq("id", m.rowId));
    }
  } catch (e) {
    return { kind: "createdButNotWritten", assignmentId,
             reason: "Next week was created and is hidden; its sets were written, and moving a row to its next exercise "
               + `failed: ${(e as Error).message}. That row keeps its old exercise.` };
  }
  await setHidden(db, assignmentId, false);
  return { kind: "wrote", assignmentId, rows: binding.writes.length, setsWritten: p.writes.length,
           setsRemoved: p.deletions.length, moves: p.moves.length, rowEdits: p.rowEdits.length };
}

// ── The runner — `CoachAutoApplyRunner.run(_:now:)` for ONE client ─────────────────────────────────────────────────────

export interface DayReport {
  rootWorkoutId: string;
  targetDate: string;
  outcome: Outcome | { kind: "notWritten"; reason: string; code?: string };
  /** What the coach's review says first — the deload, a pain proposal, the ladder, OFF (`readingOrder.prefix(4)`). */
  said: string[];
}

/** F15 4.2 — an AUTO program proposes a deload (the numbers, or the 7th/8th week): ONE open proposal per client and week
 *  (`coach_deload_proposals`, PK client + Monday), written as the coach, its reason the verdict's sentence.
 *  `ignoreDuplicates`: a proposal already there — open, accepted or declined — is left as it is, so a "Not now" is never
 *  re-asked the same week. */
export async function proposeDeload(db: Db, coachId: string, clientId: string, weekStartDay: string,
                                    byTrigger = true): Promise<void> {
  await exec(db.from("coach_deload_proposals").upsert(
    { client_id: clientId, coach_id: coachId, week_start: weekStartDay,
      reason: byTrigger ? TRIGGER_SENTENCE : CALENDAR_SENTENCE },
    { onConflict: "client_id,week_start", ignoreDuplicates: true }));
}

/** F15 4.2 — THE ONE TAP: "Deload this week". Every assignment of the client in the week from `weekStartDay` that is not
 *  yet trained (no logged set) and not already a deload is planned as a deload over ITS OWN rows (no clone — the week is
 *  already given), bound onto those rows, its sets written before any is removed, and marked `is_deload`. The proposal is
 *  then `accepted`. A day that cannot be bound is reported and left as it is. */
export async function deloadWeek(db: Db, clientId: string, weekStartDay: string):
    Promise<{ days: { assignmentId: string; date: string; outcome: string }[] }> {
  const start = mondayOf(weekStartDay);
  const end = new Date(Date.parse(`${start}T00:00:00Z`) + 6 * 86_400_000).toISOString().slice(0, 10);
  const given = await rows<{ id: string; workout_id: string; root_workout_id: string | null; scheduled_date: string | null;
                             is_deload: boolean | null }>(
    db.from("coach_assignments").select("id, workout_id, root_workout_id, scheduled_date, is_deload")
      .eq("client_id", clientId).gte("scheduled_date", start).lte("scheduled_date", end));
  const days: { assignmentId: string; date: string; outcome: string }[] = [];
  for (const a of given) {
    const date = (a.scheduled_date ?? "").slice(0, 10);
    if (a.is_deload === true) { days.push({ assignmentId: a.id, date, outcome: "already a deload" }); continue; }
    const done = await logged(db, clientId, a.scheduled_date);
    if (done !== null && done.rows.some((r) => r.sets.length > 0)) {
      days.push({ assignmentId: a.id, date, outcome: "already trained" }); continue;
    }
    const engine = await readEngine(db, a.root_workout_id ?? a.workout_id);
    const own = await rowsOfWorkout(db, a.workout_id, { ...engine, progress: true });
    const built = plan({
      day: { assignmentId: a.id.toLowerCase(), date: isoOfDay(a.scheduled_date), rootWorkoutId:
             (a.root_workout_id ?? a.workout_id).toLowerCase(), rows: own.rows },
      logged: null, history: [], musclesByExerciseId: {}, checkIn: EMPTY_CHECK_IN, weekNumber: 1, isDeload: true,
      mint: (() => { let n = 0; return () => `introduced-${++n}`; })(),
    });
    const r = review(built);
    const decisions = automaticDecisions(r);
    const binding = bind(r, decisions, own.rows);
    if (binding.kind !== "bound") { days.push({ assignmentId: a.id, date, outcome: explain(binding) }); continue; }
    const persistence = persist(binding.writes, own.setsByRow);
    if (persistence.kind !== "ready") { days.push({ assignmentId: a.id, date, outcome: persistence.reason }); continue; }
    const p = persistence.persistence;
    if (p.writes.length > 0) {
      await exec(db.from("coach_workout_sets").upsert(p.writes.map((w) => ({
        id: w.id ?? newSetId(), workout_exercise_id: w.rowId, position: w.set.position, reps: w.set.reps,
        weight_kg: w.set.weightKg, time_seconds: w.set.timeSeconds, rest_seconds: w.set.restSeconds, set_type: w.set.setType,
      })), { onConflict: "id" }));
    }
    if (p.deletions.length > 0) await exec(db.from("coach_workout_sets").delete().in("id", p.deletions));
    await markDeload(db, a.id);
    days.push({ assignmentId: a.id, date, outcome: "deload" });
  }
  await exec(db.from("coach_deload_proposals").update({ decided: "accepted", decided_at: new Date().toISOString() })
    .eq("client_id", clientId).eq("week_start", start));
  return { days };
}

export async function runForClient(db: Db, clientId: string, today: string,
                                   coachId: string | null = null): Promise<{ due: Due[]; days: DayReport[] }> {
  const assigned = await rows<{ workout_id: string; root_workout_id: string | null; scheduled_date: string | null;
                                hidden_from_client: boolean | null }>(
    db.from("coach_assignments").select("workout_id, root_workout_id, scheduled_date, hidden_from_client")
      .eq("client_id", clientId));
  const owed = due(assigned.map((a): Assigned => ({
    rootWorkoutId: (a.root_workout_id ?? a.workout_id).toLowerCase(),
    scheduledDate: a.scheduled_date,
    hiddenFromClient: a.hidden_from_client === true,
  })), today);
  const days: DayReport[] = [];
  for (const d of owed) {
    let said: string[] = [];
    try {
      const prepared = await prepare(db, { rootWorkoutId: d.rootWorkoutId, clientId, weekNumber: d.weekNumber,
                                           targetDate: d.targetDate });
      said = readingOrderOf(prepared.review).slice(0, 4);
      if (prepared.program?.proposed === true && coachId !== null) {
        /* ⚠️ NEVER FATAL: a proposal is advice beside the week; a failed write is logged, the day is still written. */
        await proposeDeload(db, coachId, clientId, prepared.program.weekStart, prepared.program.byTrigger)
          .catch((e) => console.error("nextWeekWriter: the deload proposal was not written:", (e as Error).message));
      }
      days.push({ rootWorkoutId: d.rootWorkoutId, targetDate: d.targetDate,
                  outcome: await applyAutomatically(db, prepared, d.targetDate), said });
    } catch (e) {
      // A 23505 is another device (or this endpoint, twice) having written the day first — the index is the idempotence.
      const err = e as WriterError;
      days.push({ rootWorkoutId: d.rootWorkoutId, targetDate: d.targetDate,
                  outcome: { kind: "notWritten", reason: err.message, code: err.code }, said });
    }
  }
  return { due: owed, days };
}

